import type { FC, ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

import { AuthClient } from '@skytech/auth'
import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { InfoBox, Input, LanguageChoiceButtons, MessagePopup, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import {
  COUNTRY_FLAGS,
  countryOptions,
  getDefaultCountryId,
  getWhiteLabelForDealerStyle,
} from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import InfoOutlineIcon from 'mdi-react/InfoOutlineIcon'
import { useLocation, useNavigate } from 'react-router-dom'

import authResources from '@/api/auth'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import useLogIn from '@/hooks/useLogin'
import { appRoutes } from '@/util/misc'

import AccessCodeView from './AccessCodeView'
import PasswordView from './PasswordView'

import './Login.styles.scss'

type VisibleStep = 'userName' | 'password' | 'accessCode'

type FormFields = {
  userName: string
  phoneCountryId: number
  password?: string
  accessCode?: string
}

const Login: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const location = useLocation()
  const { whiteLabel, locale, setLocale } = useAppContext()

  const searchParams = new URLSearchParams(location.search)
  const nextUrl = searchParams.get('next') || appRoutes.home
  // possible error msg coming from the azure integration
  const azureErrorMessage = searchParams.get('azureErrorMsg')

  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const [visibleStep, setVisibleStep] = useState<VisibleStep>('userName')
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: ReactElement
    title: ReactElement
  } | null>(null)

  const loginMutation = useLogIn()
  const {
    data: loginSettings,
    isInitialLoading: isLoginSettingsLoading,
    refetch: fetchLoginSettings,
  } = useQuery(
    queries.loginSettings(),
    () => {
      if (!formRef.current) throw Error('Missing form values')
      return authResources.getSettings({
        phoneNumber: formRef.current?.values.userName,
        phoneCountryId: formRef.current?.values.phoneCountryId,
      })
    },
    {
      enabled: false,
      staleTime: Infinity,
      onSuccess({ isPasswordRequired, hasMsalLogin }) {
        if (hasMsalLogin) {
          const host = window.location.host
          window.location.replace(
            `${config.AUTH_API_URL}azure-msal/signin/dealer?host=${host}&pathname=${nextUrl}`
          )
          return
        }

        setVisibleStep(isPasswordRequired ? 'password' : 'accessCode')
      },
      onError() {
        setErrorPopupMessage({
          text: <Translation id="Something went wrong. Please refresh the page and try again." />,
          title: <Translation id="Loading login settings error" />,
        })
      },
    }
  )

  useEffect(() => {
    if (AuthClient.getToken()) {
      navigate('/')
    }
  }, [])

  const handleSubmit = (values: FormFields) => {
    if (visibleStep === 'userName') {
      fetchLoginSettings()
      return
    }

    if (visibleStep === 'password' && loginSettings?.isAccessCodeRequired) {
      setVisibleStep('accessCode')
      return
    }

    loginMutation.mutate(values, {
      onSuccess() {
        navigate(nextUrl)
      },
      onError: () => {
        setErrorPopupMessage({
          text: <Translation id="Login failed. Code incorrect or expired." />,
          title: <Translation id="Login error" />,
        })
      },
    })
  }

  const handleClickPrev = () => {
    if (visibleStep === 'password') {
      setVisibleStep('userName')
    }

    if (visibleStep === 'accessCode') {
      if (loginSettings?.isPasswordRequired) {
        setVisibleStep('password')
      } else {
        setVisibleStep('userName')
      }
    }
  }

  useEffect(() => {
    if (!azureErrorMessage) return

    setErrorPopupMessage({
      title: <Translation id="Login attempt via Azure failed" />,
      text: (
        <>
          <Translation id="Something went wrong when logging in, please try again!" />{' '}
          <Translation id="Error" />: {azureErrorMessage}
        </>
      ),
    })
  }, [navigate, azureErrorMessage])

  const submitButtonText =
    (visibleStep === 'password' && !loginSettings?.isAccessCodeRequired) ||
    visibleStep === 'accessCode'
      ? 'Log in'
      : 'Continue'
  const isSubmitButtonLoading =
    visibleStep === 'userName' ? isLoginSettingsLoading : loginMutation.isLoading
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)
  const defaultPhoneCountryId = getDefaultCountryId(locale)

  return (
    <div className={`login-form-wrapper ${whiteLabel}`}>
      {environmentBadgeLabel && (
        <div className="development-info-container">
          <InfoBox emphasis="warning" whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}>
            <InfoOutlineIcon />
            <span className="development-text">
              <Translation
                id={
                  config.ENVIRONMENT === 'demo'
                    ? 'This is a demo version of the app.'
                    : 'This is a development version of the app.'
                }
              />
            </span>
          </InfoBox>
        </div>
      )}

      <div className="login-form">
        <div className="logo" />

        <div>
          <h1>
            <Translation id="Welcome to Dealer!" />
          </h1>
        </div>

        <Formik
          innerRef={formRef}
          validateOnBlur={false}
          initialValues={{
            userName: '',
            phoneCountryId: defaultPhoneCountryId,
            password: '',
            accessCode: '',
          }}
          validate={values => {
            const errors: Partial<Record<keyof FormFields, string | ReactElement>> = {}

            if (!values.userName) {
              errors.userName = <Translation id="This field is mandatory" />
            }

            if (
              !values.password &&
              loginSettings?.isPasswordRequired &&
              visibleStep === 'password'
            ) {
              errors.password = <Translation id="This field is mandatory" />
            }

            if (
              !values.accessCode &&
              loginSettings?.isAccessCodeRequired &&
              visibleStep === 'accessCode'
            ) {
              errors.accessCode = <Translation id="This field is mandatory" />
            }

            return errors
          }}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <div className="flex gap-2">
                {visibleStep !== 'userName' && (
                  <div className="login-info">
                    <div className="sent-code-message">
                      <Translation
                        id="Logging in as {userName}"
                        userName={<span className="user-name">{values.userName}</span>}
                      />
                    </div>
                  </div>
                )}

                {visibleStep === 'userName' && (
                  <>
                    <Field name="phoneCountryId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          id="phoneCountryId"
                          options={countryOptions.map(option => ({
                            value: option.value,
                            label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                          }))}
                          marginBottom={16}
                          {...field}
                          onChange={option => {
                            form.setFieldValue(field.name, option?.value)
                            // To preselect phone country code on the login page
                            localStorage.setItem('phoneCountryId', String(option?.value))
                          }}
                        />
                      )}
                    </Field>

                    <Field name="userName">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          id="userName"
                          wrapperClassName="grow"
                          ariaLabel={translate('Username')}
                          marginBottom={16}
                          touched={meta.touched}
                          error={meta.error}
                          placeholder={translate('Enter your username')}
                          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                          {...field}
                        />
                      )}
                    </Field>
                  </>
                )}
              </div>

              {visibleStep === 'password' && <PasswordView />}

              {visibleStep === 'accessCode' && <AccessCodeView userName={values.userName} />}

              {/* todo: change back to footer tag when global styles are removed */}
              <div className="-mx-2 my-1 flex flex-col justify-between">
                <div className="flex justify-between">
                  {visibleStep === 'userName' ? (
                    <div />
                  ) : (
                    <Button
                      size="medium"
                      onClick={handleClickPrev}
                      variant="tertiary"
                      whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                    >
                      <Translation id="Back" />
                    </Button>
                  )}

                  <Button
                    type="submit"
                    isIconRight
                    size="medium"
                    data-test="login-button"
                    Icon={<ArrowRightIcon />}
                    // No other ground on which loading could be shown when the redirection to the Azure platform happens
                    isLoading={isSubmitButtonLoading || loginSettings?.hasMsalLogin}
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                  >
                    <Translation id={submitButtonText} />
                  </Button>
                </div>

                <LanguageChoiceButtons locale={locale} onChangeLocale={setLocale} />
              </div>
            </Form>
          )}
        </Formik>

        <MessagePopup
          message={errorPopupMessage?.text}
          title={errorPopupMessage?.title}
          isOpen={Boolean(errorPopupMessage?.text)}
          onClose={() => setErrorPopupMessage(null)}
          whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
        />
      </div>
    </div>
  )
}

export default Login
